import { useEffect, useRef } from 'react';

/** Six single-character cells that advance and retreat as the design shows. */
export default function OtpInput({ value, onChange, onComplete, invalid = false, length = 6 }) {
  const refs = useRef([]);

  useEffect(() => {
    refs.current[0]?.focus();
  }, []);

  const digits = Array.from({ length }, (_, index) => value[index] ?? '');

  function commit(next) {
    onChange(next);
    // A full code counts as leaving the field, so its error can show without
    // needing a blur the user has no reason to perform.
    if (next.length === length && onComplete) onComplete();
  }

  function setDigit(index, raw) {
    const digit = raw.replace(/\D/g, '').slice(-1);
    const next = digits.slice();
    next[index] = digit;
    commit(next.join('').slice(0, length));
    if (digit && index < length - 1) refs.current[index + 1]?.focus();
  }

  function onKeyDown(index, event) {
    if (event.key === 'Backspace' && !digits[index] && index > 0) refs.current[index - 1]?.focus();
    if (event.key === 'ArrowLeft' && index > 0) refs.current[index - 1]?.focus();
    if (event.key === 'ArrowRight' && index < length - 1) refs.current[index + 1]?.focus();
  }

  /** Pasting the whole code fills every cell rather than only the focused one. */
  function onPaste(event) {
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!pasted) return;
    event.preventDefault();
    commit(pasted);
    refs.current[Math.min(pasted.length, length - 1)]?.focus();
  }

  return (
    <div className="otp-cells" onPaste={onPaste} role="group" aria-label={`${length}-digit code`}>
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(element) => {
            refs.current[index] = element;
          }}
          className="input"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          aria-label={`Digit ${index + 1}`}
          aria-invalid={invalid ? 'true' : undefined}
          value={digit}
          style={{ borderColor: invalid ? 'var(--color-accent-2)' : digit ? 'var(--color-accent)' : undefined }}
          onChange={(event) => setDigit(index, event.target.value)}
          onKeyDown={(event) => onKeyDown(index, event)}
          onBlur={() => {
            // Only the last cell losing focus means the user has left the group.
            if (index === length - 1 && onComplete) onComplete();
          }}
        />
      ))}
    </div>
  );
}
